import type { PipeTransform } from '@angular/core';
import { inject, Pipe } from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import type { OfferItem } from '@core/offers-types';

import type { Counter } from './offers-footer-counters/offers-footer-counters.component';
import { CounterTypes } from './offers-footer-counters/offers-footer-counters.component';
import type { OfferCardFields } from './types';
import { offerCardVisibleFieldsFallback } from './types';

export enum CandidateCounter {
  'NewCandidates' = 'new_candidates',
  'QualifiedCandidates' = 'qualified_candidates',
  'DisqualifiedCandidates' = 'disqualified_candidates',
  'HiredCandidates' = 'hired_candidates',
}

@Pipe({ standalone: true, name: 'extractOfferCounters' })
export class ExtractOfferCountersPipe implements PipeTransform {
  private i18n = inject(I18nService);

  public transform(
    offer: OfferItem,
    visibleFields: Record<OfferCardFields, boolean> = offerCardVisibleFieldsFallback,
  ): Counter[] {
    return [
      this.getCandidateCounter(
        CandidateCounter.QualifiedCandidates,
        offer.qualifiedCandidatesCount,
        visibleFields.qualified_candidates,
      ),
      this.getCandidateCounter(
        CandidateCounter.NewCandidates,
        offer.newCandidatesCount!,
        visibleFields.new_candidates,
      ),
      this.getCandidateCounter(
        CandidateCounter.HiredCandidates,
        offer.hiredCandidatesCount,
        visibleFields.hires,
      ),
      this.getCandidateCounter(
        CandidateCounter.DisqualifiedCandidates,
        offer.disqualifiedCandidatesCount,
        visibleFields.disqualified_candidates,
      ),
    ];
  }

  private getCandidateCounter(
    counter: CandidateCounter,
    count: number,
    isVisible: boolean,
  ): Counter {
    // eslint-disable-next-line default-case
    switch (counter) {
      case CandidateCounter.NewCandidates:
        return {
          value: count,
          label: this.i18n.translate('app.common.new'),
          type: CounterTypes.CounterWithBudget,
          isVisible: isVisible && count > 0,
        };
      case CandidateCounter.QualifiedCandidates:
        return {
          value: count,
          label: this.i18n.translate('app.offers.actions.x_qualified_candidates', {
            count: count,
          }),
          type: CounterTypes.CounterWithLabel,
          isVisible,
        };
      case CandidateCounter.DisqualifiedCandidates:
        return {
          value: count,
          label: this.i18n.translate('app.offers.actions.x_disqualified_candidates', {
            count: count,
          }),
          type: CounterTypes.CounterWithIcon,
          options: { iconName: 'block' },
          isVisible: isVisible && count > 0,
        };
      case CandidateCounter.HiredCandidates:
        return {
          value: count,
          label: this.i18n.translate('app.offers.actions.hires_candidates', {
            count: count,
          }),
          type: CounterTypes.CounterWithIcon,
          options: { iconName: 'hired' },
          isVisible: isVisible && count > 0,
        };
    }
  }
}
